import { call } from '@advanza/api'
import { Button } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import InputField from '@trustoo/components/input/InputField'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import clsx from 'clsx'
import RoiCalculatorHistoryModal from 'components/providers/RoiCalculatorHistoryModal'
import Restricted from 'components/Restricted'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Live, client-side ROI calculator used by sales reps during prospect calls
 * (TRUS-8284). It replaces the Excel sheet the team used to demonstrate the
 * return a provider can expect from a Trustoo subscription.
 *
 * Results update on every keystroke (no submit). On top of the live demo, the
 * rep can optionally **Save** the current figures: each save appends an
 * immutable snapshot (visible in the History modal with timestamps) and syncs
 * the values to the provider's HubSpot contact — restored in TRUS-8284 after
 * product feedback.
 *
 * On mount we fetch the latest saved snapshot (to restore the last saved state)
 * and, when none exists, pre-fill "Leads per month" with the provider's real
 * derived lead volume.
 */

interface RoiInputs {
    revenuePerJob: string
    marginPct: string
    conversionPct: string
    leadsPerMonth: string
    costPerLead: string
    subscription: string
}

const DEFAULT_INPUTS: RoiInputs = {
    revenuePerJob: '2000',
    marginPct: '30',
    conversionPct: '15',
    leadsPerMonth: '12',
    costPerLead: '25',
    subscription: '29',
}

const HIGH_CONVERSION_THRESHOLD = 30

interface RoiSnapshotResponse {
    has_snapshot?: boolean
    aov?: number | null
    margin_pct?: number | null
    expected_conversion_pct?: number | null
    cost_per_lead?: number | null
    leads_per_month?: number | null
    subscription?: number | null
    requests_per_month?: number | null
    suggested_subscription?: number | null
    suggested_cost_per_lead?: number | null
    last_updated?: string | null
}

interface RoiOutputs {
    effectiveMargin: number
    monthlyTrustooCost: number
    jobsWon: number
    monthlyRevenue: number
    marginEarned: number
    netRoi: number
    /** `null` when monthly cost is 0 — no meaningful multiple to show. */
    roiMultiple: number | null
    /**
     * Number of jobs needed to break even, rounded up. `null` means "not
     * applicable" (no jobs are ever won), which the UI renders as "–".
     */
    breakEvenJobs: number | null
    /**
     * True when effective margin ≤ cost per lead, the exact "impossible"
     * condition defined by the ticket (TRUS-8284), in which case the UI shows a
     * warning instead of a number.
     *
     * Note: this — like {@link breakEvenJobs} — intentionally compares per-job
     * margin against the raw per-lead cost and does NOT scale cost by the
     * conversion rate. At conversion < 100% the real cost per won job is higher
     * (`costPerLead / conversion`), so this is an optimistic simplification. It
     * is kept deliberately because the ticket specifies the formula verbatim;
     * changing it to a conversion-adjusted model is a product decision, not a
     * bug fix.
     */
    breakEvenImpossible: boolean
}

/**
 * `call()` rejects with the parsed JSON error body for non-success responses
 * and only rejects with a real Error on network failures, so read the message
 * off the payload instead of relying on `instanceof Error`.
 */
function extractApiErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof Error) {
        return err.message
    }
    if (typeof err === 'object' && err !== null) {
        const message = (err as { message?: unknown }).message
        if (typeof message === 'string' && message.length > 0) {
            return message
        }
    }
    return fallback
}

/**
 * Parses a raw input string into a non-negative number. Empty or unparseable
 * input is treated as 0, and negatives are clamped to 0 because no input in this
 * tool is meaningfully negative.
 */
function parseInput(value: string): number {
    const sanitized = value.replace(',', '.').replace(/[^0-9.]/g, '')
    const parsed = Number.parseFloat(sanitized)
    if (!Number.isFinite(parsed) || parsed < 0) {
        return 0
    }
    return parsed
}

/**
 * Pure ROI calculation. Kept separate from the component so the formulas can be
 * reasoned about and unit-tested in isolation. Every branch is guarded against
 * division by zero so the UI never has to render NaN, Infinity or a negative
 * break-even value.
 */
export function calculateRoi(inputs: RoiInputs): RoiOutputs {
    const revenue = parseInput(inputs.revenuePerJob)
    const marginPct = parseInput(inputs.marginPct)
    const conversionPct = parseInput(inputs.conversionPct)
    const leads = parseInput(inputs.leadsPerMonth)
    const costPerLead = parseInput(inputs.costPerLead)
    const subscription = parseInput(inputs.subscription)

    const effectiveMargin = revenue * (marginPct / 100)
    const monthlyTrustooCost = leads * costPerLead + subscription
    const jobsWon = leads * (conversionPct / 100)
    const monthlyRevenue = jobsWon * revenue
    const marginEarned = jobsWon * effectiveMargin
    const netRoi = marginEarned - monthlyTrustooCost
    const roiMultiple = monthlyTrustooCost > 0 ? marginEarned / monthlyTrustooCost : null

    // Break-even per the ticket (TRUS-8284): `subscription / (effectiveMargin -
    // costPerLead)`, with "impossible" defined as `effectiveMargin <=
    // costPerLead`. This deliberately uses the raw per-lead cost rather than the
    // conversion-adjusted cost per won job (`costPerLead / conversion`), so the
    // figure is optimistic at conversion < 100%. The formula is specified
    // verbatim by the ticket; see RoiOutputs.breakEvenImpossible for rationale.
    const marginPerLead = effectiveMargin - costPerLead
    const breakEvenImpossible = marginPerLead <= 0

    const breakEvenJobs = (() => {
        // No jobs are ever won → the concept of breaking even doesn't apply.
        if (jobsWon <= 0) {
            return null
        }
        if (breakEvenImpossible) {
            return null
        }
        return Math.ceil(subscription / marginPerLead)
    })()

    return {
        effectiveMargin,
        monthlyTrustooCost,
        jobsWon,
        monthlyRevenue,
        marginEarned,
        netRoi,
        roiMultiple,
        breakEvenJobs,
        breakEvenImpossible,
    }
}

const nlInteger = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 })
const nlOneDecimal = new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
})

/** NL locale integer euro, e.g. `€ 2.000` / `-€ 2.000` (period thousands sep). */
function formatEuro(value: number): string {
    const rounded = Math.round(value)
    const sign = rounded < 0 ? '-' : ''
    return `${sign}€ ${nlInteger.format(Math.abs(rounded))}`
}

function snapshotToInputs(data: RoiSnapshotResponse): RoiInputs {
    return {
        revenuePerJob: String(data.aov ?? ''),
        marginPct: String(data.margin_pct ?? ''),
        conversionPct: String(data.expected_conversion_pct ?? ''),
        leadsPerMonth: String(data.leads_per_month ?? ''),
        costPerLead: String(data.cost_per_lead ?? ''),
        subscription: String(data.subscription ?? ''),
    }
}

function inputsEqual(a: RoiInputs, b: RoiInputs): boolean {
    return (Object.keys(a) as Array<keyof RoiInputs>).every(
        (key) => parseInput(a[key]) === parseInput(b[key]),
    )
}

/**
 * Client-side mirror of the table validator (TRUS-8284) so the Save button is
 * disabled before the request rather than after a rejection.
 */
function getValidationError(inputs: RoiInputs): string | null {
    if ((Object.keys(inputs) as Array<keyof RoiInputs>).some((key) => inputs[key].trim() === '')) {
        return 'Please fill in all fields before saving.'
    }
    if (parseInput(inputs.marginPct) > 1000) {
        return 'Margin must be 1000% or lower.'
    }
    if (parseInput(inputs.conversionPct) > 100) {
        return 'Conversion rate must be between 0 and 100%.'
    }
    return null
}

interface NumberFieldProps {
    label: string
    value: string
    onChange: (value: string) => void
    prefix?: string
    suffix?: string
}

const NumberField = ({ label, value, onChange, prefix, suffix }: NumberFieldProps) => (
    <div>
        <div className="text-sm text-grey-700 mb-1">{label}</div>
        <InputField
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            inputClassName="w-full"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            before={prefix ? <span className="text-grey-700">{prefix}</span> : undefined}
            after={suffix ? <span className="text-grey-700">{suffix}</span> : undefined}
        />
    </div>
)

interface MetricCardProps {
    label: string
    value: string
    highlighted?: boolean
}

const MetricCard = ({ label, value, highlighted = false }: MetricCardProps) => (
    <div
        className={clsx(
            'rounded-lg border p-4',
            highlighted ? 'border-brand-200 bg-brand-50' : 'border-grey-200',
        )}>
        <div className="text-sm text-grey-700">{label}</div>
        <div className="text-xl font-bold text-dark">{value}</div>
    </div>
)

interface RoiCalculatorProps {
    providerId: number
}

const RoiCalculator = ({ providerId }: RoiCalculatorProps) => {
    const [inputs, setInputs] = useState<RoiInputs>(DEFAULT_INPUTS)
    const [leadsFromHistory, setLeadsFromHistory] = useState(false)
    const [subscriptionFromCategory, setSubscriptionFromCategory] = useState(false)
    const [costPerLeadFromCategory, setCostPerLeadFromCategory] = useState(false)
    /** Last persisted inputs, or `null` when the provider has no snapshot yet. */
    const [savedInputs, setSavedInputs] = useState<RoiInputs | null>(null)
    const [lastSaved, setLastSaved] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)

    /**
     * Guard the pre-fills so each field is only ever overridden once, on the
     * initial server response — never clobbering a value the rep has since typed.
     */
    const leadsPrefilled = useRef(false)
    const subscriptionPrefilled = useRef(false)
    const costPerLeadPrefilled = useRef(false)

    useEffect(() => {
        leadsPrefilled.current = false
        subscriptionPrefilled.current = false
        costPerLeadPrefilled.current = false
        // Clear stale "pre-filled" hints so they don't linger from a previously
        // rendered provider if this component is reused without unmounting.
        setLeadsFromHistory(false)
        setSubscriptionFromCategory(false)
        setCostPerLeadFromCategory(false)
        let cancelled = false
        call(`office/providers/get-provider-roi/${providerId}`)
            .then((response) => {
                if (cancelled) {
                    return
                }
                const data = (response?.data as RoiSnapshotResponse) ?? {}
                if (data.has_snapshot) {
                    // Restore the last saved state; values come from the snapshot,
                    // not the derived pre-fills.
                    leadsPrefilled.current = true
                    subscriptionPrefilled.current = true
                    costPerLeadPrefilled.current = true
                    const restored = snapshotToInputs(data)
                    setInputs(restored)
                    setSavedInputs(restored)
                    setLastSaved(data.last_updated ?? null)
                    return
                }
                // No snapshot yet: keep static defaults but pre-fill leads/month
                // from the provider's weekly lead limit, and the subscription and
                // cost per lead from their most expensive category (TRUS-8571).
                const requestsPerMonth = data.requests_per_month
                if (
                    !leadsPrefilled.current &&
                    typeof requestsPerMonth === 'number' &&
                    Math.round(requestsPerMonth) > 0
                ) {
                    leadsPrefilled.current = true
                    setLeadsFromHistory(true)
                    setInputs((prev) => ({
                        ...prev,
                        leadsPerMonth: String(Math.round(requestsPerMonth)),
                    }))
                }
                const suggestedSubscription = data.suggested_subscription
                if (
                    !subscriptionPrefilled.current &&
                    typeof suggestedSubscription === 'number' &&
                    suggestedSubscription > 0
                ) {
                    subscriptionPrefilled.current = true
                    setSubscriptionFromCategory(true)
                    setInputs((prev) => ({
                        ...prev,
                        // Keep the exact price so it stays consistent with a
                        // restored snapshot value (presence prices may have cents).
                        subscription: String(suggestedSubscription),
                    }))
                }
                const suggestedCostPerLead = data.suggested_cost_per_lead
                if (
                    !costPerLeadPrefilled.current &&
                    typeof suggestedCostPerLead === 'number' &&
                    suggestedCostPerLead > 0
                ) {
                    costPerLeadPrefilled.current = true
                    setCostPerLeadFromCategory(true)
                    setInputs((prev) => ({
                        ...prev,
                        // Keep the exact price (per-lead prices may have cents).
                        costPerLead: String(suggestedCostPerLead),
                    }))
                }
            })
            // Best-effort only: if the lookup fails we silently keep the static
            // defaults so the calculator is always usable during a call.
            .catch((err: unknown) => console.warn('ROI load failed', err))
        return () => {
            cancelled = true
        }
    }, [providerId])

    const outputs = useMemo(() => calculateRoi(inputs), [inputs])

    const setField = (field: keyof RoiInputs) => (value: string) => {
        // Any manual edit locks out a late-arriving pre-fill and drops the
        // "pre-filled" hint, since the value is now rep-supplied.
        if (field === 'leadsPerMonth') {
            leadsPrefilled.current = true
            setLeadsFromHistory(false)
        }
        if (field === 'costPerLead') {
            costPerLeadPrefilled.current = true
            setCostPerLeadFromCategory(false)
        }
        if (field === 'subscription') {
            subscriptionPrefilled.current = true
            setSubscriptionFromCategory(false)
        }
        setInputs((prev) => ({ ...prev, [field]: value }))
    }

    const conversionTooHigh = parseInput(inputs.conversionPct) > HIGH_CONVERSION_THRESHOLD
    const validationError = getValidationError(inputs)
    const isUnchanged = savedInputs !== null && inputsEqual(inputs, savedInputs)
    const canSave = !isSaving && validationError === null && !isUnchanged

    const handleSave = () => {
        if (validationError !== null) {
            setSaveError(validationError)
            return
        }
        setSaveError(null)
        setIsSaving(true)
        call(`office/providers/save-provider-roi/${providerId}`, {
            method: 'POST',
            json: {
                aov: parseInput(inputs.revenuePerJob),
                margin_pct: parseInput(inputs.marginPct),
                expected_conversion_pct: parseInput(inputs.conversionPct),
                cost_per_lead: parseInput(inputs.costPerLead),
                leads_per_month: parseInput(inputs.leadsPerMonth),
                subscription: parseInput(inputs.subscription),
            },
        })
            .then((response) => {
                const data = (response?.data as RoiSnapshotResponse) ?? {}
                const restored = snapshotToInputs(data)
                setSavedInputs(restored)
                setLastSaved(data.last_updated ?? null)
            })
            .catch((err: unknown) =>
                setSaveError(extractApiErrorMessage(err, 'Failed to save ROI')),
            )
            .finally(() => setIsSaving(false))
    }

    const handleShowHistory = () => {
        NiceModal.show(RoiCalculatorHistoryModal, { providerId })
    }

    const netRoiColor = (() => {
        if (outputs.netRoi > 0) {
            return 'text-green-500'
        }
        if (outputs.netRoi < 0) {
            return 'text-red-600'
        }
        return 'text-dark'
    })()

    return (
        <div>
            <div className="text-md text-grey-800 mb-4">
                Live ROI calculator for sales calls. Pre-filled with standard Trustoo pricing —
                adjust the prospect&apos;s figures and every value below updates instantly. Use{' '}
                <strong>Save</strong> to store the figures (synced to HubSpot) and keep a history.
            </div>

            {/* Customer inputs */}
            <SectionLabel>Customer inputs</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
                <NumberField
                    label="Average revenue per job"
                    prefix="€"
                    value={inputs.revenuePerJob}
                    onChange={setField('revenuePerJob')}
                />
                <NumberField
                    label="Margin"
                    suffix="%"
                    value={inputs.marginPct}
                    onChange={setField('marginPct')}
                />
                <div>
                    <NumberField
                        label="Expected conversion rate"
                        suffix="%"
                        value={inputs.conversionPct}
                        onChange={setField('conversionPct')}
                    />
                    {conversionTooHigh && (
                        <div className="mt-1 text-sm text-amber-600">
                            This is above the typical range — are you sure?
                        </div>
                    )}
                </div>
            </div>

            {/* Trustoo pricing inputs */}
            <SectionLabel>Trustoo pricing</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <div>
                    <NumberField
                        label="Leads per month (est.)"
                        value={inputs.leadsPerMonth}
                        onChange={setField('leadsPerMonth')}
                    />
                    {leadsFromHistory && (
                        <div className="mt-1 text-sm text-grey-700">
                            Pre-filled from the provider&apos;s expected lead volume
                        </div>
                    )}
                </div>
                <div>
                    <NumberField
                        label="Cost per lead"
                        prefix="€"
                        value={inputs.costPerLead}
                        onChange={setField('costPerLead')}
                    />
                    {costPerLeadFromCategory && (
                        <div className="mt-1 text-sm text-grey-700">
                            Pre-filled from their most expensive category, edit if needed
                        </div>
                    )}
                </div>
                <div>
                    <NumberField
                        label="Monthly subscription"
                        prefix="€"
                        value={inputs.subscription}
                        onChange={setField('subscription')}
                    />
                    {subscriptionFromCategory && (
                        <div className="mt-1 text-sm text-grey-700">
                            Pre-filled from their most expensive subscription — edit for promotional
                            offers
                        </div>
                    )}
                </div>
            </div>

            {/* Headline ROI */}
            <div className="rounded-lg border border-grey-200 p-5 mb-4">
                <div className="text-md text-grey-800">Net ROI per month</div>
                <div className={clsx('text-4xl font-bold', netRoiColor)}>
                    {formatEuro(outputs.netRoi)}
                </div>
                {/* Point 2 (TRUS-8284): make the Net ROI calculation explicit. */}
                <div className="mt-1 text-sm text-grey-700">
                    = monthly margin earned ({formatEuro(outputs.marginEarned)}) − monthly Trustoo
                    cost ({formatEuro(outputs.monthlyTrustooCost)})
                </div>
                <div className="mt-2 text-md text-grey-800">
                    {outputs.roiMultiple === null ? (
                        '—'
                    ) : (
                        <>
                            For every €1 invested, you earn back{' '}
                            <strong>€{nlOneDecimal.format(outputs.roiMultiple)}</strong>
                        </>
                    )}
                </div>
                <div className="mt-1 text-md text-grey-800">
                    {(() => {
                        if (outputs.breakEvenImpossible) {
                            return (
                                <span className="text-amber-600">
                                    With this margin and lead cost, the investment cannot break
                                    even. Adjust the margin or cost per lead.
                                </span>
                            )
                        }
                        if (outputs.breakEvenJobs === null) {
                            return <>Break-even: —</>
                        }
                        return (
                            <>
                                You need <strong>{nlInteger.format(outputs.breakEvenJobs)}</strong>{' '}
                                {outputs.breakEvenJobs === 1 ? 'job' : 'jobs'} per month to break
                                even
                            </>
                        )
                    })()}
                </div>
            </div>

            {/* Supporting metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                    label="Monthly revenue generated"
                    value={formatEuro(outputs.monthlyRevenue)}
                    highlighted
                />
                <MetricCard
                    label="Monthly margin earned"
                    value={formatEuro(outputs.marginEarned)}
                />
                <MetricCard
                    label="Monthly Trustoo cost"
                    value={formatEuro(outputs.monthlyTrustooCost)}
                />
                <MetricCard
                    label="Effective margin per job"
                    value={formatEuro(outputs.effectiveMargin)}
                />
                <MetricCard
                    label="Estimated monthly jobs won"
                    value={nlOneDecimal.format(outputs.jobsWon)}
                />
            </div>

            {saveError && (
                <div className="mt-4 rounded bg-red-50 px-3 py-2 text-md text-red-800">
                    {saveError}
                </div>
            )}

            {/* Save / history */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <Restricted location="providers" checkRead={false}>
                    <Button appearance="primary" onClick={handleSave} disabled={!canSave}>
                        {isSaving ? <LoadingDots /> : 'Save ROI'}
                    </Button>
                </Restricted>
                <Button appearance="text_primary" iconBefore="history" onClick={handleShowHistory}>
                    History
                </Button>
                {lastSaved && (
                    <span className="text-sm text-grey-700">Last saved: {lastSaved}</span>
                )}
            </div>
        </div>
    )
}

const SectionLabel = ({ children }: { children: ReactNode }) => (
    <div className="text-sm font-bold uppercase tracking-wider text-grey-700 mb-2">{children}</div>
)

export default RoiCalculator
